import {authenticated_api} from "./base";
import {savedSearchSubmissionIntent, settleSavedSearchSubmissionIntent} from "./savedSearchSubmissionIntent";
import {
  createMockSavedSearch,
  isMockSavedSearchesEnabled,
} from "../fixture/mockSavedSearches";

const liveSavedSearchIds = new Set();

const rememberLiveSavedSearch = (savedSearch) => {
  const id = savedSearch?.id || savedSearch?._id;
  if (id) liveSavedSearchIds.add(String(id));
};

const asWorkspaceSavedSearch = (savedSearch) => ({
  ...savedSearch,
  id: String(savedSearch.id),
  record_type: "saved_search",
  source_query: savedSearch.query || {},
  ownership_mode: "query",
  company_count: null,
});

const getAllLiveSavedSearches = async () => {
  const items = [];
  let page = 1;
  while (true) {
    let response;
    try {
      response = await authenticated_api.get("/saved-searches", {params: {page, page_size: 100}});
    } catch (error) {
      response = error.response;
    }
    if (response?.status !== 200 || !Array.isArray(response.data?.items)) {
      return items.length ? {status: 200, data: {items}, partial: true} : response;
    }
    items.push(...response.data.items);
    if (items.length >= response.data.total || response.data.items.length < 100) {
      return {...response, data: {...response.data, items}};
    }
    page += 1;
  }
};

const mockWorkspaceResponse = (result) => {
  if (result.ok) {
    return {status: result.status, data: result.data};
  }
  return {status: result.status, data: {detail: result.detail}};
};

export const createWorkspaceApi = async (workspaceData) => {
  // Local design-preview: Search results are mocked and the workspace API is
  // often unreachable, which previously surfaced as a generic Save Search error.
  if (isMockSavedSearchesEnabled()) {
    return mockWorkspaceResponse(createMockSavedSearch(workspaceData));
  }

  try {
    const isLiveSavedSearch = workspaceData?.query && typeof workspaceData.query === "object";
    const result = isLiveSavedSearch
      ? await authenticated_api.post(`/saved-searches`, workspaceData)
      : await authenticated_api.post(`/workspaces`, workspaceData);
    if (isLiveSavedSearch && (result?.status === 200 || result?.status === 201)) {
      rememberLiveSavedSearch(result.data);
    }
    return result;
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') console.log(error);
    return error.response;
  }
};

export const getWorkspacesApi = async () => {
  try {
    const [workspaceResult, savedSearchResult] = await Promise.allSettled([
      authenticated_api.get(`/workspaces`),
      getAllLiveSavedSearches(),
    ]);
    const workspaces = workspaceResult.status === "fulfilled"
      ? workspaceResult.value : workspaceResult.reason?.response;
    const savedSearches = savedSearchResult.status === "fulfilled"
      ? savedSearchResult.value : savedSearchResult.reason?.response;
    const legacyRows = workspaces?.status === 200 && Array.isArray(workspaces.data)
      ? workspaces.data : [];
    const liveRows = savedSearches?.status === 200 && Array.isArray(savedSearches.data?.items)
      ? savedSearches.data.items.map(asWorkspaceSavedSearch) : [];
    liveRows.forEach(rememberLiveSavedSearch);
    if (workspaces?.status !== 200 && savedSearches?.status !== 200) {
      return workspaces?.status ? workspaces : savedSearches;
    }
    const merged = new Map(legacyRows.map((row) => [String(row.id || row._id), row]));
    liveRows.forEach((row) => merged.set(String(row.id), row));
    const rows = [...merged.values()].sort((left, right) =>
      Date.parse(right.created_at || 0) - Date.parse(left.created_at || 0)
    );
    const partial = workspaces?.status !== 200 || savedSearches?.status !== 200 || savedSearches?.partial;
    return {status: 200, data: rows, ...(partial ? {partial: true} : {})};
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') console.log(error);
    return error.response;
  }
};

export const getWorkspaceDetailApi = async (workspaceId, page = 1, pageSize = 100) => {
  try {
    const id = String(workspaceId);
    if (liveSavedSearchIds.has(id)) {
      const live = await authenticated_api.get(`/saved-searches/${id}`, {
        params: {page, page_size: Math.min(pageSize, 100)},
      });
      if (live?.status === 200) {
        return {...live, data: {...live.data, record_type: "saved_search", source_query: live.data.query || {}, ownership_mode: "query"}};
      }
      return live;
    }
    let live;
    try {
      live = await authenticated_api.get(`/saved-searches/${id}`, {
        params: {page, page_size: Math.min(pageSize, 100)},
      });
    } catch (error) {
      if (error.response?.status !== 404) throw error;
      live = error.response;
    }
    if (live?.status === 200) {
      rememberLiveSavedSearch(live.data);
      return {...live, data: {...live.data, record_type: "saved_search", source_query: live.data.query || {}, ownership_mode: "query"}};
    }
    if (live?.status !== 404) return live;
    return await authenticated_api.get(
      `/workspaces/${id}`,
      {
        params: { page, page_size: pageSize }
      }
    );
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') console.log(error);
    return error.response;
  }
};

export const renameWorkspaceApi = async (workspaceId, renameData) => {
  try {
    if (liveSavedSearchIds.has(String(workspaceId))) {
      return await authenticated_api.patch(`/saved-searches/${workspaceId}`, renameData);
    }
    return await authenticated_api.patch(
      `/workspaces/${workspaceId}`,
      renameData
    );
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') console.log(error);
    return error.response;
  }
};

export const enrichWorkspaceApi = async (workspaceId, enrichData) => {
  let submittedKey;
  try {
    if (liveSavedSearchIds.has(String(workspaceId))) {
      const payload = {...enrichData, idempotency_key: enrichData.idempotency_key
        || savedSearchSubmissionIntent(workspaceId, enrichData)};
      submittedKey = payload.idempotency_key;
      const response = await authenticated_api.post(`/saved-searches/${workspaceId}/enrich`, payload);
      settleSavedSearchSubmissionIntent(workspaceId, response, submittedKey);
      return response;
    }
    return await authenticated_api.post(
      `/workspaces/${workspaceId}/enrich`,
      enrichData);
  } catch (error) {
    if (liveSavedSearchIds.has(String(workspaceId))) {
      settleSavedSearchSubmissionIntent(workspaceId, error.response, submittedKey);
    }
    if (process.env.NODE_ENV !== 'production') console.log(error);
    return error.response;
  }
};

// BE #517: an upper-bound credit quote bound to the account, frozen company set,
// model and price version. Its quote_token goes back on enrichWorkspaceApi.
export const quoteWorkspaceEnrichmentApi = async (workspaceId, quoteData) => {
  try {
    if (liveSavedSearchIds.has(String(workspaceId))) {
      return await authenticated_api.post(`/saved-searches/${workspaceId}/enrich/quote`, quoteData);
    }
    return await authenticated_api.post(
      `/workspaces/${workspaceId}/enrich/quote`,
      quoteData);
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') console.log(error);
    return error.response;
  }
};

export const forkWorkspaceApi = async (workspaceId, forkData) => {
  try {
    const result = await authenticated_api.post(
      `/workspaces/${workspaceId}/fork`,
      forkData);
    if (result?.status === 200 || result?.status === 201) rememberLiveSavedSearch(result.data);
    return result;
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') console.log(error);
    return error.response;
  }
};

export const deleteWorkspaceApi = async (workspaceId) => {
  try {
    if (liveSavedSearchIds.has(String(workspaceId))) {
      return await authenticated_api.delete(`/saved-searches/${workspaceId}`);
    }
    return await authenticated_api.delete(`/workspaces/${workspaceId}`);
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') console.log(error);
    return error.response;
  }
};



export const forkByPromptApi = async (workspaceId, promptData) => {
  try {
    return await authenticated_api.post(
      `/workspaces/${workspaceId}/fork-by-prompt`,
      promptData);
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') console.log(error);
    return error.response;
  }
};

export const saveDraftToWorkspaceApi = async (draftSessionId, draftData) => {
  try {
    return await authenticated_api.post(
      `/workspaces/from-draft/${draftSessionId}`,
      draftData);
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') console.log(error);
    return error.response;
  }
};

export const getFilterBatchStatusApi = async (batchId) => {
  try {
    return await authenticated_api.get(`/filter-batch/${batchId}`);
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') console.log(error);
    return error.response;
  }
};

export const getFilterBatchResultsApi = async (batchId) => {
  try {
    return await authenticated_api.get(`/filter-batch/${batchId}/results`);
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') console.log(error);
    return error.response;
  }
};

export const ASSISTANT_WORKFLOW_RUN_TIMEOUT_MS = 10 * 60 * 1000;

export const getWorkspaceAssistantRecommendationsApi = async (workspaceId) => {
  try {
    return await authenticated_api.get(
      `/workspaces/${workspaceId}/assistant/recommendations`
    );
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') console.log(error);
    return error.response;
  }
};

export const previewWorkspaceAssistantPlanApi = async (workspaceId, previewData) => {
  try {
    return await authenticated_api.post(
      `/workspaces/${workspaceId}/assistant/plan-preview`,
      previewData
    );
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') console.log(error);
    return error.response;
  }
};

export const createWorkspaceAssistantWorkflowApi = async (workspaceId, workflowData) => {
  try {
    return await authenticated_api.post(
      `/workspaces/${workspaceId}/assistant/workflows`,
      workflowData
    );
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') console.log(error);
    return error.response;
  }
};

export const getWorkspaceAssistantWorkflowsApi = async (workspaceId, limit = 5, offset = 0) => {
  try {
    return await authenticated_api.get(
      `/workspaces/${workspaceId}/assistant/workflows`,
      {
        params: {limit, offset}
      }
    );
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') console.log(error);
    return error.response;
  }
};

export const getWorkspaceAssistantWorkflowApi = async (workspaceId, workflowId) => {
  try {
    return await authenticated_api.get(
      `/workspaces/${workspaceId}/assistant/workflows/${workflowId}`
    );
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') console.log(error);
    return error.response;
  }
};

export const approveWorkspaceAssistantWorkflowApi = async (workspaceId, workflowId) => {
  try {
    return await authenticated_api.post(
      `/workspaces/${workspaceId}/assistant/workflows/${workflowId}/approve`
    );
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') console.log(error);
    return error.response;
  }
};

export const runWorkspaceAssistantWorkflowApi = async (workspaceId, workflowId) => {
  try {
    return await authenticated_api.post(
      `/workspaces/${workspaceId}/assistant/workflows/${workflowId}/run`,
      undefined,
      {timeout: ASSISTANT_WORKFLOW_RUN_TIMEOUT_MS}
    );
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') console.log(error);
    return error.response;
  }
};

export const cancelWorkspaceAssistantWorkflowApi = async (workspaceId, workflowId) => {
  try {
    return await authenticated_api.post(
      `/workspaces/${workspaceId}/assistant/workflows/${workflowId}/cancel`
    );
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') console.log(error);
    return error.response;
  }
};
